import { produce } from 'immer';
import { createWithEqualityFn } from 'zustand/traditional';
import {
  BACKLIT_CODE,
  BACKLIT_VALUE_CODES,
  MATERIAL_S_CODE,
  NOT_SECONDE_SURFACE,
  PATTERN_SETS,
  PRINTED_SIDE_CODE,
  PRINTED_SIDE_VALUE_CODES,
  RESET_CAMERA,
  SIZE_CODE,
  S_COLOR_CODE,
  S_CO_BRANDING,
  S_INK_COLOR_CODE,
  S_QTY_CODE,
} from '../constants';
import * as THREE from 'three';
import {
  changeInchTo3dUnit,
  ColorTheme,
  getPatternSetSurfaces,
  loadTexture,
  loadTextureWithReplacedColors,
  TextureSurface,
} from '../helpers';
import configService from '../../config/config.service';
import useThemeStore from '../hooks/use-theme-store';

export interface SpecValue {
  code: string;
  key: string;
  section: {
    itemId: string;
    code: string;
    id: string;
    surfaces: Surface[];
    surfaceIds: string[];
    isMain: boolean;
    isSemitransparent: boolean;
  };
}

export interface Spec extends SpecValue {
  specsValue: SpecValue[];
}
export interface ProductSpec extends Spec {
  specValue?: SpecValue;
}
export interface Product {
  specs: ProductSpec[];
}

export interface Surface {
  id: string;
  code: string;
  surfaceOrder: number;
  facePatternTypeCode: string;
  texture?: THREE.Texture;
  url?: string;
  width?: number;
  height?: number;
}
export interface Section {
  id: string;
  code?: string;
  surfaces?: Surface[];
  surfaceIds?: string[];
  itemId?: string;
  visible: boolean;
  product: Product;
  modelId: number;
  isMain?: boolean;
  isSemitransparent?: boolean;
  isSwagProduct?: boolean;
  brandTextures?: THREE.Texture[];
  sizeBrandTextures?: { [key: string]: THREE.Texture[] };
  isBacklit?: boolean;
  hasBacklitSpec?: boolean;
  diffSectionDesigns?: boolean;
}
export type Location = {
  x: number;
  y: number;
  z: number;
  rotation: number;
};

export type TModel = {
  id: number;
  mainSectionId?: string;
  src: string;
  code: string;
  isSizeSpecExist?: boolean;
  location: Location;
  sections: Section[];
  dimensions: {
    height: number;
    length: number;
    width: number;
  };
};

export type TBundle = {
  isSingle: boolean;
  isApparel: boolean;
  isHumanExist: boolean;
  floorLength: number;
  floorWidth: number;
  colorTheme: ColorTheme;
  isFloorPlanModelExist: boolean;
  models: TModel[];
  surfaceIds: string[];
  patternSetSurfaces: Record<string, string>;
};
export type PrintedSideSpecData = {
  model: number;
  section: string;
  removedSurfaces: string[];
  addedSurfaces: string[];
  isOutAsSingleSide: boolean | string;
} | null;

export type SwagProductData = {
  model: number;
  section: string;
  color?: string;
  inkColor?: string;
} | null;
export interface BundleStore {
  bundle: TBundle | null;
  focusedSurface: string | undefined;
  shouldFocusSurface: boolean;
  enableZoom: boolean;
  setEnableZoom: (enableZoom: boolean) => void;
  init: (bundle: TBundle, rest?: { itemsHaveBacklit?: string[]; showBackLight: boolean }) => void;
  setActiveSectionVisibility: (value: { sectionId: string; visible: boolean }) => boolean;
  changeSpace: (value: { sectionId: string; specCode: string; specValueCode: string }) => boolean;
  printedSideSpecData: PrintedSideSpecData;
  swagProductData: SwagProductData;
  updateHumanPosition: (length: number, timeout: number) => void;
  reset: () => void;
  itemsHaveBacklit: string[];
  lightBox: boolean;
  setLightBox: (value: boolean) => void;
  showBackLight?: boolean;
  setDiffSectionDesigns: (modelId: number, sectionId: string) => void;
  updatePatternSetOrColorTheme: (patternSet: any, action: string) => void;
  isSceneRotationEnabled: boolean;
  setIsSceneRotationEnabled: (value: boolean) => void;
}

export const calculateRushTurnaroundValue = ({
  total,
  onTotalPercent,
  qty,
}: {
  total: number;
  qty: number;
  onTotalPercent: number;
}) => {
  const onTotalPriceValue = (onTotalPercent * total) / 100;
  return { itemTotal: (onTotalPriceValue + total) * qty, onTotalPrice: onTotalPriceValue * qty };
};

const initialState = {
  bundle: null,
  focusedSurface: RESET_CAMERA,
  shouldFocusSurface: false,
  printedSideSpecData: null,
  swagProductData: null,
  enableZoom: false,
  itemsHaveBacklit: [],
  lightBox: false,
  showBackLight: false,
};

export const useBundleStore = createWithEqualityFn<BundleStore>((set, get) => ({
  ...initialState,
  init: (bundle, rest) => set({ bundle, ...rest }),
  setEnableZoom: (enableZoom) => set({ enableZoom }),
  setActiveSectionVisibility: ({ sectionId, visible }) => {
    if (get()?.bundle?.models) {
      set(
        produce((state: BundleStore) => {
          const bundle = state?.bundle as TBundle;
          bundle?.models?.forEach((model: TModel) => {
            const section = model.sections.find(
              (section) => section?.id?.split('_2ND_')?.[0] === sectionId,
            );
            if (section) {
              section.visible = visible;
            }
          });

          if (state.focusedSurface != RESET_CAMERA) {
            state.focusedSurface = RESET_CAMERA;
            state.shouldFocusSurface = !state.shouldFocusSurface;
          } else {
            if (state?.bundle?.isApparel) {
              setTimeout(() => {
                window.dispatchEvent(new CustomEvent('reset3dView'));
              }, 200);
            }
          }
        }),
      );
      return true;
    }
    return false;
  },
  updateHumanPosition: (length, timeout) => {
    setTimeout(() => {
      set(
        produce((state: BundleStore) => {
          const bundle = state?.bundle as TBundle;
          bundle?.models?.forEach((m: TModel) => {
            if (m?.code === 'HUMAN') {
              m.location.z = length / 2 + changeInchTo3dUnit(m?.dimensions?.length) / 2 + 0.5;
            }
          });
        }),
      );
    }, timeout);
  },
  changeSpace: ({ sectionId, specCode, specValueCode }) => {
    if (get()?.bundle?.models) {
      set(
        produce((state: BundleStore) => {
          const bundle = state?.bundle as TBundle;
          bundle?.models?.forEach((model: TModel) => {
            const section = model?.sections.find(
              (section) => section?.id?.split('_2ND_')?.[0] === sectionId,
            );
            if (section) {
              const specs = section?.product?.specs;
              const spec = specs?.find((spec: Spec) => spec?.code === specCode);

              if (spec) {
                const specValue = spec?.specsValue?.find(
                  (specValue) => specValue?.code === specValueCode,
                );
                const modelId = model?.id;
                if (specValue) {
                  const nSVCode = specValueCode;
                  const pSVCode = spec?.specValue?.code;

                  //manage Swag products Color spec
                  if (specCode === S_COLOR_CODE) {
                    const color = '#' + nSVCode;
                    state.swagProductData = {
                      model: modelId,
                      section: section?.id,
                      color,
                      inkColor: undefined,
                    };
                  }

                  //manage backlit spec
                  if (specCode === BACKLIT_CODE) {
                    const isBacklit = nSVCode === BACKLIT_VALUE_CODES[0] ? true : false;
                    state.itemsHaveBacklit = isBacklit
                      ? [...state.itemsHaveBacklit, sectionId]
                      : state.itemsHaveBacklit.filter((id) => id !== sectionId);
                    section.isBacklit = isBacklit;

                    window.dispatchEvent(
                      new CustomEvent('changeBacklit', {
                        detail: {
                          lightBox: state?.lightBox,
                          sectionId,
                          modelId,
                          isBacklit: state?.lightBox && isBacklit,
                        },
                      }),
                    );
                  }

                  //manage Material
                  if (specCode === MATERIAL_S_CODE) {
                    window.dispatchEvent(
                      new CustomEvent('changeMaterial', {
                        detail: {
                          sectionId: section?.id,
                          modelId,
                          color: nSVCode,
                        },
                      }),
                    );
                  }

                  //manage Swag products Ink Color spec
                  if (specCode === S_INK_COLOR_CODE) {
                    const color = '#' + nSVCode;
                    state.swagProductData = {
                      model: modelId,
                      section: section?.id,
                      color: undefined,
                      inkColor: color,
                    };
                  }

                  //manage Qty spec
                  if (specCode === S_QTY_CODE) {
                    window.dispatchEvent(
                      new CustomEvent('changeDuplicatedSectionVisible', {
                        detail: {
                          qty: nSVCode,
                          sectionId: section?.id,
                          modelId,
                        },
                      }),
                    );
                  }

                  //manage co-branding-spec
                  if (specCode === S_CO_BRANDING) {
                    section?.surfaces?.map((surface, index) => {
                      const texture =
                        nSVCode === 'YES' ? section?.brandTextures?.[index] : surface?.texture;
                      if (texture) {
                        window.dispatchEvent(
                          new CustomEvent('changeSectionBranding', {
                            detail: {
                              surfaceId: surface?.id,
                              modelId,
                              texture,
                              isSemitransparent: section?.isSemitransparent,
                              isSwagProduct: section?.isSwagProduct,
                            },
                          }),
                        );
                      }
                    });
                  }

                  //check printed side spec
                  let isDoubleSide = false;
                  const insideKey = PRINTED_SIDE_VALUE_CODES[0];
                  const outsideKey = PRINTED_SIDE_VALUE_CODES[1];
                  const doubleSideKey = PRINTED_SIDE_VALUE_CODES[2];
                  if (specCode === PRINTED_SIDE_CODE) {
                    const isInside = nSVCode === insideKey;
                    const isOutAsSingleSide = nSVCode === outsideKey;

                    const firstSurface = section?.surfaces?.[0]?.id;
                    const secondeSurface = section?.surfaces?.[1]?.id;
                    const doubleSideSpecValue = isOutAsSingleSide ? firstSurface : secondeSurface;

                    const ides = bundle?.surfaceIds;
                    const addedSurface = pSVCode === insideKey ? secondeSurface : firstSurface;

                    const focusedSurface = addedSurface
                      ? addedSurface
                      : firstSurface + '_2ND_' + 'BACK';

                    if (isInside || isOutAsSingleSide) {
                      if (!section?.diffSectionDesigns) {
                        bundle.surfaceIds =
                          pSVCode === doubleSideKey
                            ? ides?.filter((id) => id !== doubleSideSpecValue)
                            : ([
                                ...ides?.filter((id) => id !== doubleSideSpecValue),
                                addedSurface,
                              ] as string[]);
                      }

                      state.printedSideSpecData = {
                        model: modelId,
                        section: section?.id,
                        removedSurfaces: [NOT_SECONDE_SURFACE],
                        addedSurfaces: [],
                        isOutAsSingleSide,
                      };

                      if (isOutAsSingleSide && pSVCode !== doubleSideKey) {
                        isDoubleSide = true;
                        state.focusedSurface = focusedSurface;
                        state.shouldFocusSurface = !state.shouldFocusSurface;
                      }
                    } else {
                      isDoubleSide = true;
                      state.focusedSurface = focusedSurface;
                      state.shouldFocusSurface = !state.shouldFocusSurface;
                      if (!section?.diffSectionDesigns) {
                        bundle.surfaceIds = [...ides, addedSurface as string];
                      }
                      state.printedSideSpecData = {
                        model: modelId,
                        section: section?.id,
                        removedSurfaces: [],
                        addedSurfaces: [NOT_SECONDE_SURFACE],
                        isOutAsSingleSide: pSVCode === insideKey ? false : true,
                      };
                    }
                  }

                  if (
                    state.focusedSurface != RESET_CAMERA &&
                    !isDoubleSide &&
                    specCode === PRINTED_SIDE_CODE
                  ) {
                    state.focusedSurface = RESET_CAMERA;
                    state.shouldFocusSurface = !state.shouldFocusSurface;
                  }

                  //start Shape spec
                  const shapeSpecEvent = (sectionId: string) => {
                    window.dispatchEvent(
                      new CustomEvent('changeShapeSpec', {
                        detail: {
                          sectionId,
                          modelId,
                          specCode: specCode,
                          specValueCode: nSVCode,
                          specs,
                        },
                      }),
                    );
                  };

                  if (specCode !== SIZE_CODE) {
                    shapeSpecEvent(section?.id);
                  }
                  //end Shape spec

                  //manage size spec
                  if (specCode === SIZE_CODE) {
                    const newSection = specValue?.section;
                    const newId = newSection?.itemId;

                    //manage position of the human for single product
                    if (bundle.isSingle && bundle?.isHumanExist) {
                      window.dispatchEvent(
                        new CustomEvent('changeHumanPosition', {
                          detail: { id: newId },
                        }),
                      );
                    }

                    section.id = newId;
                    section.code = newSection?.code;
                    section.isMain = newSection?.isMain;
                    section.isSemitransparent = newSection?.isSemitransparent;
                    section.surfaces = newSection?.surfaces;

                    //manage co-branding-spec
                    const coBrandingSpec = specs?.find((s) => s.code === S_CO_BRANDING);
                    if (coBrandingSpec) {
                      const brandTextures = section?.sizeBrandTextures?.[nSVCode];
                      section.brandTextures = brandTextures;
                      newSection?.surfaces?.map((surface, index) => {
                        const texture =
                          coBrandingSpec.specValue?.code === 'YES'
                            ? brandTextures?.[index]
                            : surface?.texture;
                        if (texture) {
                          window.dispatchEvent(
                            new CustomEvent('changeSectionBranding', {
                              detail: {
                                surfaceId: surface?.id,
                                modelId,
                                texture,
                                isSemitransparent: section?.isSemitransparent,
                                isSwagProduct: section?.isSwagProduct,
                                isSizeChange: true,
                              },
                            }),
                          );
                        }
                      });
                    }

                    //manage printed side spec
                    const printedSideSpec = specs?.find((s) => s.code === PRINTED_SIDE_CODE);
                    const pSSVCode = printedSideSpec?.specValue?.code;
                    const isInside = pSSVCode === insideKey;
                    const isOutSide = pSSVCode === outsideKey;
                    const isSingleSide = isOutSide || isInside;

                    //Shape spec
                    shapeSpecEvent(newId);

                    const secondSurface = newSection?.surfaceIds?.[1];

                    bundle.surfaceIds = state?.bundle?.surfaceIds
                      ?.filter((id) => !spec.specValue?.section?.surfaceIds?.includes(id))
                      .concat(
                        isInside
                          ? newSection?.surfaceIds.slice(0, 1)
                          : isOutSide
                            ? secondSurface
                              ? newSection?.surfaceIds.slice(1, 2)
                              : []
                            : newSection?.surfaceIds,
                      ) as string[];

                    const removedSurfaces = isSingleSide ? [NOT_SECONDE_SURFACE] : [];
                    const addedSurfaces = isSingleSide ? [] : [NOT_SECONDE_SURFACE];
                    const isOutAsSingleSide = isSingleSide ? isOutSide : 'both';

                    state.printedSideSpecData = {
                      model: modelId,
                      section: newSection?.itemId,
                      removedSurfaces,
                      addedSurfaces,
                      isOutAsSingleSide,
                    };

                    setTimeout(() => {
                      const prevState = useBundleStore.getState();
                      useBundleStore.setState({
                        focusedSurface: RESET_CAMERA,
                        shouldFocusSurface: !prevState.shouldFocusSurface,
                      });
                    }, 100);
                  }
                  spec.specValue = specValue;
                }
              }
            }
          });
        }),
      );
      return true;
    }
    return false;
  },
  reset: () => set(initialState),
  setLightBox: (lightBox) => {
    set({ lightBox });
  },
  setDiffSectionDesigns: (modelId, sectionId) => {
    set(
      produce((state: BundleStore) => {
        const model = state?.bundle?.models?.find((model) => model?.id === modelId);
        const section = model?.sections?.find((section) => section?.id === sectionId) as Section;

        if (section) {
          section.diffSectionDesigns = true;

          if (state.bundle) {
            const secondeSurface = section?.surfaces?.[1]?.id;
            state.bundle.surfaceIds = [
              ...state?.bundle?.surfaceIds,
              ...(secondeSurface ? [secondeSurface] : []),
            ];
          }
        }
      }),
    );
  },
  updatePatternSetOrColorTheme: async (patternSetOrColorTheme, action) => {
    const state = get();
    const models = state?.bundle?.models;
    const isPatternSet = action === 'patternSet';

    if (!models) return;

    const patternSetSurfaces = isPatternSet
      ? getPatternSetSurfaces(patternSetOrColorTheme?.items)
      : state?.bundle?.patternSetSurfaces;

    const colorTheme = isPatternSet
      ? (state?.bundle?.colorTheme as ColorTheme)
      : patternSetOrColorTheme;

    // Deep clone models so we can safely mutate
    const clonedModels = deepCloneWithoutTextures(models);

    await Promise.all(
      clonedModels.map(async (m: any) => {
        if (!m.sections) return;

        await Promise.all(
          m.sections.map(async (s: any) => {
            if (!s.surfaces) return;

            // 🧩 handle size specs (await all)
            const sizeSpec = s?.product?.specs?.find((spec: any) => spec?.code === SIZE_CODE);

            if (sizeSpec) {
              await Promise.all(
                sizeSpec.specsValue.map(async (sv: any) => {
                  const section = sv?.section;
                  if (!section?.surfaces) return;

                  await loadSectionTextures(section, patternSetSurfaces, colorTheme);
                }),
              );
            }
          }),
        );
      }),
    );

    set(
      produce((state: BundleStore) => {
        if (state.bundle) {
          state.bundle.models = clonedModels;
          if (!isPatternSet) {
            state.bundle.colorTheme = colorTheme;
          } else {
            state.bundle.patternSetSurfaces = patternSetSurfaces as Record<string, string>;
          }
        }
      }),
    );
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('resetSurfaces'));
    }, 0);
  },
  isSceneRotationEnabled: true,
  setIsSceneRotationEnabled: (value) => {
    set(
      produce((state: BundleStore) => {
        state.isSceneRotationEnabled = value;
      }),
    );
  },
}));

function deepCloneWithoutTextures(obj: any): any {
  if (obj === null || typeof obj !== 'object') return obj;

  if (obj instanceof HTMLImageElement) return undefined; // skip
  if (obj.isTexture) return undefined; // skip Three.js texture

  if (Array.isArray(obj)) {
    return obj.map(deepCloneWithoutTextures);
  }

  const clone: any = {};
  for (const key in obj) {
    if (key === 'texture' || key === 'image' || key === 'map') continue; // skip problematic props
    clone[key] = deepCloneWithoutTextures(obj[key]);
  }
  return clone;
}

const loadSectionTextures = async (s: any, patternSetSurfaces: any, colorTheme: ColorTheme) => {
  const shopCode = useThemeStore.getState().themeCode;
  // 🧩 load main section textures
  await Promise.all(
    s.surfaces.map(async (sur: any) => {
      const imageId = patternSetSurfaces?.[sur?.facePatternTypeCode];

      if (imageId) {
        try {
          const url = `${configService.getBaseUrl(shopCode).bucketStorage}/${PATTERN_SETS}/${imageId}`;
          const texture = colorTheme
            ? await loadTextureWithReplacedColors(url, {
                [PATTERN_SET_COLORS.PRIMARY]: colorTheme?.primaryColor,
                [PATTERN_SET_COLORS.SECONDARY]: colorTheme?.secondaryColor,
                [PATTERN_SET_COLORS.TERTIARY]: colorTheme?.tertiaryColor,
                [PATTERN_SET_COLORS.ACCENT]: colorTheme?.accentColor,
              })
            : await loadTexture(url);
          sur.texture = texture;
        } catch (error) {
          console.error('Error loading texture', error);
        }
      } else {
        sur.texture = undefined;
      }
    }),
  );
};

export const PATTERN_SET_COLORS = {
  PRIMARY: '#1E88E5',
  SECONDARY: '#43A047',
  TERTIARY: '#FB8C00',
  ACCENT: '#E53935',
};
